import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type CalculationConstructionDetailsDto from "../../dto/calculations/CalculationConstructionDetailsDto";
import type CalculationListItemDto from "../../dto/calculations/CalculationListItemDto";
import type CalculationResultDto from "../../dto/calculations/CalculationResultDto";
import markGreatestLoadCaseValue from "./constructionCalculationsNewSliceHelpers";
import type MastResultDto from "../../dto/calculations/MastResultDto";
import { validateDrillholesCodeWrapper } from "./constructionCalculationsNewSliceThunks";
import { computeDrillholesValue } from "./components/calculationConstructionDetails/field/drillholescode/utils/DrillholesCodeUtils";
import type CalculationStatus from "../../types/CalculationStatus";
import type DrillholeDto from "../../dto/calculations/DrillholeDto";


export interface ConstructionCalculationsState {
  constructionDetails: CalculationConstructionDetailsDto | undefined;
  calculations: CalculationListItemDto[];
  selectedCalculationId: string | undefined;
  selectedMastNo: string | undefined;
  selectedCalculationResults: CalculationResultDto | undefined;
  nextId: string | undefined;
  prevId: string | undefined;
}

const initialState: ConstructionCalculationsState = {
    constructionDetails: undefined,
    calculations: [],
    selectedCalculationId: undefined,
    selectedMastNo: undefined,
    selectedCalculationResults: undefined,
    nextId: undefined,
    prevId: undefined
}

const constructionCalculationsNewSlice = createSlice({
    name: "constructionCalculationsNew",
    initialState,
    reducers: {
        load: (
            state,
            {
                payload,
            }: PayloadAction<{
                currentCalculationId: string | undefined;
                calculations: CalculationListItemDto[];
                constructionDetails: CalculationConstructionDetailsDto | undefined;
                currentCalculationResults: CalculationResultDto | undefined;
                currentMastNo: string | undefined;
                nextId: string | undefined;
                prevId: string | undefined;
            }>,
        ) => {
                state.calculations = payload.calculations;
                state.nextId = payload.nextId;
                state.prevId = payload.prevId;
                state.constructionDetails = payload.constructionDetails;
                state.selectedCalculationId = payload.currentCalculationId;
                state.selectedMastNo = payload.currentMastNo;
                state.selectedCalculationResults = markGreatestLoadCaseValue(payload.currentCalculationResults);
        },
        switchMast: (state, {payload}: PayloadAction<string>) => {
            state.selectedMastNo = payload;
        },
        clear: state => {
            state.calculations = [];
            state.selectedCalculationId = undefined;
            state.selectedMastNo = undefined;
            state.selectedCalculationResults = undefined;
            state.constructionDetails = undefined
            state.nextId = undefined;
            state.prevId = undefined;
        },
        updateStatus: (
          state,
          {
            payload,
          }: PayloadAction<{
            calculationId: string;
            status: CalculationStatus;
          }>
        ) => {
          const index = state.calculations.findIndex(
            c => c.calculationId === payload.calculationId,
          );

          if (index !== -1) {
            state.calculations[index].status = payload.status;
          }
        },
        updateSavedDrillholesCode: (
              state,
              {payload}: PayloadAction<{mastId: string}>,
        ) => {
          const mastResult = state.selectedCalculationResults?.mastResults?.find(
            (mr: MastResultDto) => mr.mastId === payload.mastId,
          );
          if (!mastResult) return;

          mastResult.savedDrillholesCode = {
            ...mastResult.drillholesCode,
            drillholes: [...mastResult.drillholesCode.drillholes.map(dh => ({...dh}))],
          };
        },
        revertDrillholesCodeToPreviousState: (
          state,
          {payload}: PayloadAction<{mastId: string}>,
        ) => {
          const mastResult = state.selectedCalculationResults?.mastResults?.find(
            (mr: MastResultDto) => mr.mastId === payload.mastId,
          );
          if (!mastResult) return;

          mastResult.drillholesCode = {
            ...mastResult.savedDrillholesCode,
            drillholes: [...mastResult.savedDrillholesCode.drillholes.map(dh => ({...dh}))],
          };
        },
        commitEditedDrillholes: (
          state,
          {
            payload,
          }: PayloadAction<{
            mastId: string;
            drillholes: DrillholeDto[];
          }>,
        ) => {
          const mastResult = state.selectedCalculationResults?.mastResults?.find(
            (mr: MastResultDto) => mr.mastId === payload.mastId,
          );
          if (!mastResult) return;

          const drillholes = payload.drillholes;
          mastResult.drillholesCode.drillholes = drillholes;
          mastResult.drillholesCode.value = computeDrillholesValue(drillholes);
          mastResult.savedDrillholesCode = {
            ...mastResult.drillholesCode,
            drillholes: [...drillholes.map(dh => ({...dh}))],
          };
        },
        updateData: (
            state,
            {
              payload,
            }: PayloadAction<{
              id: string;
              results: CalculationResultDto;
            }>,
        ) => {
            state.selectedCalculationResults = markGreatestLoadCaseValue(payload.results);
      
            const calculationIndex = state.calculations.findIndex(c => c.calculationId === payload.id);
            const prevSelectedIndex = state.calculations.findIndex(c => c.selected);
      
            state.calculations[prevSelectedIndex].selected = false;
            state.selectedCalculationId = state.calculations[calculationIndex]?.calculationId;
      
            if (
              calculationIndex !== undefined &&
              state.calculations[calculationIndex] !== undefined
            ) {
              state.calculations[calculationIndex].selected = true;
            }
        },
        removeById: (state, {payload}: PayloadAction<string>) => {
          state.calculations = state.calculations.filter(
            c => c.calculationId !== payload,
          );
        },
    },
    extraReducers: builder => {
      builder.addCase(
        validateDrillholesCodeWrapper.fulfilled,
        (state, action) => {
          const currentMast = state.selectedCalculationResults?.mastResults?.find(
            (m: MastResultDto) => m.mastId === action.payload.mastId,
          );

          if (currentMast?.drillholesCode !== undefined) {
            currentMast.drillholesCode.isValid = action.payload.isDrillholesCodeValid;
          }
        },
      );
    }
  }
);

export const { load, switchMast, clear, updateSavedDrillholesCode, revertDrillholesCodeToPreviousState, commitEditedDrillholes, updateData, updateStatus, removeById } = constructionCalculationsNewSlice.actions;
export default constructionCalculationsNewSlice.reducer;
